import { CREATIVE_TYPE } from '@ad-lucy/config/creatives';
import { GlobalPayout } from '@ad-lucy/utils/types';
import type { CreativeId } from '../creatives/types';
import type { CreativesTrackingSettings } from './types';

export const adSpecificCreativesTrackingConfig: Partial<
  Record<CreativeId, CreativesTrackingSettings>
> = {
  [CREATIVE_TYPE.BP]: {
    forceGlobalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    globalTrackingMetaDataDefaults: {
      globalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    },
  },
  [CREATIVE_TYPE.SA]: {
    forceGlobalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    globalTrackingMetaDataDefaults: {
      globalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    },
  },
  [CREATIVE_TYPE.VV]: {
    forceGlobalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    globalTrackingMetaDataDefaults: {
      globalPayout: GlobalPayout.NO_PAYOUT_ON_HOVER,
    },
  },
};

// TODO: Move all default payout values / triggers usw. to this configuration to extract it from code base + creatives config and make everything available within tracking context
export const creativesTrackingConfig: CreativesTrackingSettings = {
  globalTrackingMetaDataDefaults: {
    globalPayout: GlobalPayout.NONE,
  },
  builderComponents: {
    storeFinder: {
      click: [
        {
          description: 'Click anywhere on storefinder component/tile except searchInput',
          config: {
            defaultPayout: true,
            trigger: 'storeFinder',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on SearchInput',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'storeFinder-searchInput',
          },
          payoutMetaValue: 'searchClickPayout',
        },
        {
          description: 'Click on a searchResult',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'storeFinder-storeChange_select',
          },
          payoutMetaValue: 'storeClickPayout',
        },
      ],
    },
    playlist: {},
    videoControls: {
      click: [
        {
          description: 'Click on mute video',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-vastVideo_mute',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on unmute video',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-vastVideo_unmute',
          },
          docImageId: 'unmuteClickPayout',
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    textConfigure: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
          },
          payoutMetaValue: 'clickPayout',
          triggerFunction: (componentProps) => `content-${componentProps.component.name}`,
        },
        {
          description: 'Click on component to fold out text without linkout',
          config: {
            defaultPayout: true,
            external: false,
          },
          payoutMetaValue: 'clickPayout',
          triggerFunction: (componentProps) => `content-${componentProps.component.name}`,
        },
      ],
    },
    sliderContainer: {},
    textWithIcon: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-textWithIcon',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    spotlightsArea: {
      click: [
        {
          description: 'Click on spotlight marker',
          config: {
            defaultPayout: true,
            trigger: 'content-spotlightsArea',
          },
          payoutMetaValue: (componentProps) =>
            (componentProps.parent?.meta as Record<string, boolean>)?.clickPayout ?? undefined,
        },
      ],
      hover: [
        {
          description: 'Hover over spotlight marker',
          config: {
            defaultPayout: true,
            trigger: 'content-spotlightsArea',
            once: true,
            external: false,
            minHoverDuration: 0,
          },
          payoutMetaValue: (componentProps) =>
            (componentProps.parent?.meta as Record<string, boolean>)?.hoverPayout ?? undefined,
        },
      ],
    },
    productSwiper: {},
    callToAction: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-callToAction',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'content-callToAction',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    button: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'content-button',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'content-button',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    category: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'header-mainMenu',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on listitem',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'header-mainMenu_link',
          },
          payoutMetaValue: 'clickChildPayout',
        },
      ],
    },
    imageComparator: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-imageComparator',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component to left and right(must move to both directions)',
          config: {
            defaultPayout: true,
            trigger: 'content-imageComparator',
            minHoverDuration: 100,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    closeAd: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'header-close-ad',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'header-close-ad',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
            once: true,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    copyright: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'footer-copyright',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    countdown: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-countdown',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'content-countdown',
            minHoverDuration: 2000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    custom: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: false,
          },
          triggerFunction: (componentProps) => `content-custom-${componentProps.meta.category}`,
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    footerLogo: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'footer-logo',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'footer-logo',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    headerLogo: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'header-logo',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'header-logo',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    iframe: {
      click: [
        {
          description:
            'Tracks only the first iframe click since subsequent clicks are not reliably detected (event only fires on window focus loss)',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-iframe',
            once: true,
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    html: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-html',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    image: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-image',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on mobileIcon',
          config: {
            defaultPayout: true,
            trigger: 'content-image_mobileIcon',
            external: false,
          },
          docImageId: 'mobileIconClickPayout',
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: true,
            trigger: 'content-image',
            minHoverDuration: 2000,
            once: true,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    imageAndText: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-imageAndText',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    imageMap: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-imageMap',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      zoom: [
        {
          description: 'Zoom on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-imageMap_zoom',
            once: true,
          },
          payoutMetaValue: 'zoomPayout',
        },
      ],
    },
    imageMapArea: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: false,
            trigger: 'content-imageMapArea',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'content-imageMapArea',
            once: true,
            external: false,
            minHoverDuration: 0,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    spotlights: {
      click: [
        {
          description: 'Click on main component (whole tile)',
          config: {
            defaultPayout: true,
            trigger: 'content-spotlights',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    infoModal: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-infoModal',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on Modal close Button',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-closeModal',
          },
          docImageId: 'closeModalClickPayout',
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component (only if mouseoverActivation is true)',
          config: {
            defaultPayout: true,
            trigger: 'content-infoModal',
            minHoverDuration: 0,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    link: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'header-location',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    mediaControls: {
      click: [
        {
          description: 'Mute media',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-mediaControls_mute',
          },
          docImageId: 'muteClickPayout',
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Unmute media',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-mediaControls_unmute',
          },
          docImageId: 'unmuteClickPayout',
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click play media',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-mediaControls_play',
          },
          docImageId: 'playClickPayout',
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click pause media',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-mediaControls_pause',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over mute button',
          config: {
            defaultPayout: false,
            trigger: 'footer-mediaControls_mute',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
            once: true,
          },
          docImageId: 'muteHoverPayout',
          payoutMetaValue: 'hoverPayout',
        },
        {
          description: 'Hover over play button',
          config: {
            defaultPayout: false,
            trigger: 'footer-mediaControls_play',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
            once: true,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    modal: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-link_modal',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on close modal button',
          config: {
            defaultPayout: false,
            payout: false,
            external: false,
            trigger: 'modal-closeModal',
          },
          docImageId: 'closeModalClickPayout',
          payoutMetaValue: 'clickPayout',
          trackingMetaValues: (componentProps) => ({
            label: componentProps.component.name,
          }),
        },
      ],
    },
    pricePatch: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'content-pricePatch',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: true,
            trigger: 'content-pricePatch',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    programmaticHpa: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'content-programmaticHpa',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    slider: {
      click: [
        {
          description: 'Click on arrows or pagination dots',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-sliderNavigation',
          },
          payoutMetaValue: (_componentProps, globalConfig) =>
            globalConfig?.meta?.gridSlider_clickPayout,
        },
        {
          description: 'Click on free space in component (outside of any content)',
          config: {
            defaultPayout: true,
            trigger: 'content-slider',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    search: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'header-searchMenu',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on list item',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'header-searchResult',
          },
          payoutMetaValue: 'clickChildPayout',
        },
        {
          description: 'Click on no result and go to shop',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'header-searchMenu_searchNoResultToShop',
          },
          payoutMetaValue: 'noResultToShopClickPayout',
        },
      ],
      searchNoResult: [
        {
          description: 'No searchResult found',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'header-searchMenu_searchNoResult',
          },
          payoutMetaValue: 'noResultPayout',
        },
      ],
    },
    socialFlyoutBar: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-socialFlyoutBar',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: true,
            trigger: 'footer-socialFlyoutBar',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    socialMediaBar: {
      click: [
        {
          description:
            'Click on component, only triggered, when there is not enough space for all icons and socialFlyoutBar is displayed. On direct Icon click footer-link_socials tracker is triggered',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-socialMediaBar',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description:
            'Hover over component, only triggered, when there is not enough space for all icons and socialFlyoutBar is displayed. On direct Icon hover footer-link_socials tracker is triggered',
          config: {
            defaultPayout: true,
            trigger: 'footer-socialMediaBar',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    socialmedia: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
          },
          payoutMetaValue: 'clickPayout',
          triggerFunction: (componentProps) =>
            `footer-link_socials_${componentProps.meta.category}`,
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
          triggerFunction: (componentProps) =>
            `footer-link_socials_${componentProps.meta.category}`,
        },
      ],
    },
    socialmediaFooter: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
          },
          payoutMetaValue: 'clickPayout',
          triggerFunction: (componentProps) =>
            `footer-link_socials_${componentProps.meta.category}`,
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
          triggerFunction: (componentProps) =>
            `footer-link_socials_${componentProps.meta.category}`,
        },
      ],
    },
    stickyCTA: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-stickyCTA',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: true,
            trigger: 'content-stickyCTA',
            minHoverDuration: 2000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    stickyImage: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-stickyImage',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: true,
            trigger: 'content-stickyImage',
            minHoverDuration: 2000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    stickyText: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-stickyText',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    textButton: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-textButton',
            external: true,
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'content-textButton',
            minHoverDuration: 2000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    store: {
      click: [
        {
          description: 'Click on Routeplanner',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchNavigationModal',
          },
          payoutMetaValue: 'routePlannerClickPayout',
        },
        {
          description: 'Select branch from modal',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChange_select',
          },
          docImageId: 'storeSelectClickPayout',
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Click on Market Link',
          config: {
            defaultPayout: true,
          },
          payoutMetaValue: 'toMarketClickPayout',
          externalFunction: (_componentProps, defaults) => defaults?.linkOutOnStoreClick ?? true,
          triggerFunction: (_componentProps, defaults) =>
            defaults?.defaultAndDetailsClickTrackerTrigger ?? 'content-branchWebsiteLink',
        },
        {
          description: 'Click on branch change Button',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChange',
          },
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Click on E-mail',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchEmailLink',
          },
          payoutMetaValue: () => true,
        },
        {
          description: 'Close branch select modal',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-closeModal',
          },
          payoutMetaValue: () => true,
        },
      ],
    },
    enhancedStore: {
      click: [
        {
          description: 'Click on Routeplanner',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchNavigationModal',
          },
          payoutMetaValue: 'routePlannerClickPayout',
        },
        {
          description: 'Click on Store Details',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'content-branchStoreDetailsModal',
          },
          payoutMetaValue: () => false,
        },
        {
          description: 'Click on change store button',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChangeModal',
          },
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Select branch from modal',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChange_select',
          },
          docImageId: 'storeSelectClickPayout',
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Close storechange Modal',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-closeModal',
          },
          payoutMetaValue: () => true,
        },
        {
          description: 'Click on store details section',
          config: {
            defaultPayout: true,
            trigger: 'content-branchDetails',
          },
          payoutMetaValue: 'clickPayout',
          docImageId: 'storeDetailsSectionClickPayout',
        },
        {
          description: 'Click on store picture',
          config: {
            defaultPayout: true,
            trigger: 'content-branchPicture',
          },
          payoutMetaValue: 'clickPayout',
          docImageId: 'storePictureClickPayout',
        },
      ],
    },
    storeV2: {
      click: [
        {
          description: 'Click on routeplanner',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchNavigationModal',
          },
          payoutMetaValue: 'routePlannerClickPayout',
        },
        {
          description: 'Click on change market button',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChange_select',
          },
          docImageId: 'storeChangeSelectClickPayout',
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Click on market Link',
          config: {
            defaultPayout: true,
          },
          payoutMetaValue: 'toMarketClickPayout',
          externalFunction: (_componentProps, defaults) => defaults?.linkOutOnStoreClick ?? true,
          triggerFunction: (_componentProps, defaults) =>
            defaults?.defaultAndDetailsClickTrackerTrigger ?? 'content-branchWebsiteLink',
        },
        {
          description: 'Select another branch',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchChange',
          },
          payoutMetaValue: 'storeChangeClickPayout',
        },
        {
          description: 'Click on Email Link',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-branchEmailLink',
          },
          payoutMetaValue: () => true,
        },
        {
          description: 'Close store modal',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-closeModal',
          },
          payoutMetaValue: () => true,
        },
      ],
    },
    storeMenu: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'header-location',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    terms: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
          },
          payoutMetaValue: 'clickPayout',
          triggerFunction: (componentProps) =>
            `footer-link_${componentProps.meta.text?.replace(/\s/g, '') ?? ''}`,
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
          triggerFunction: (componentProps) =>
            `footer-link_${componentProps.meta.text?.replace(/\s/g, '') ?? ''}`,
        },
      ],
    },
    text: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-text',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    headerTitle: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-headerTitle',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    textV2: {},
    hover: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-hover',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on mobileIcon',
          config: {
            defaultPayout: true,
            trigger: 'content-hover_mobileIcon',
            external: false,
          },
          docImageId: 'mobileIconClickPayout',
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover on component',
          config: {
            defaultPayout: true,
            trigger: 'content-hover',
            minHoverDuration: 2000,
            maxHoverDuration: 4000,
            once: true,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    vastVideoLight: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-vastVideoLight',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    video: {
      other: [
        {
          description: 'Start the video',
          config: {
            defaultPayout: false,
            trigger: 'content-video',
            external: false,
            event: 'VideoPlayStart',
          },
          payoutMetaValue: 'startPlayPayout',
        },
        {
          description: 'Finish watching video',
          config: {
            defaultPayout: false,
            trigger: 'content-video',
            external: false,
            event: 'VideoPlay100Percent',
          },
          payoutMetaValue: 'videoPlay100PercentPayout',
        },
      ],
    },
    vastVideoXL: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-vastVideoXL',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    viewLater: {
      click: [
        {
          description: 'Submit the viewLater form',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'footer-showLater_formSubmit',
          },
          payoutMetaValue: 'submitPayout',
        },
        {
          description: 'Click on component to open form',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'footer-link_showLater',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Close the viewLater form',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'footer-showLater_formSubmit_close',
          },
          payoutMetaValue: () => false,
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            trigger: 'footer-link_showLater',
            minHoverDuration: 1500,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    logo: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-logo',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    likeButton: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-likeButton',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'content-likeButton',
            minHoverDuration: 0,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    heading: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-heading',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    sharingButton: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-sharingButton',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on dialog',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-sharingButton-dialog',
          },
          docImageId: 'dialogClickPayout',
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Close the dialog modal',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'content-sharingButton-close',
          },
          payoutMetaValue: () => false,
        },
      ],
    },
    sharingButtonFooter: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-sharingButtonFooter',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on dialog',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-sharingButtonFooter-dialog',
          },
          docImageId: 'dialogClickPayout',
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Close the dialog modal',
          config: {
            defaultPayout: false,
            external: false,
            trigger: 'content-sharingButtonFooter-close',
          },
          payoutMetaValue: () => false,
        },
      ],
    },
    tag: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'content-tag',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
    },
    productCard: {
      click: [
        {
          description: 'Click on product card',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-productCard',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on product card with enableExpand=false',
          config: {
            defaultPayout: true,
            external: true,
            trigger: 'content-productCard',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Click on product card category',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-productCard-category',
          },
          payoutMetaValue: 'clickPayout',
          docImageId: 'categoryClickPayout',
        },
      ],
    },
    productCardProduct: {
      click: [
        {
          description: 'Click on product',
          config: {
            defaultPayout: true,
            trigger: 'content-productCardProduct',
          },
          payoutMetaValue: 'clickPayout',
          docImageId: 'productClickPayout',
        },
        {
          description: 'Click on mobile Layer Icon',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-productCardProduct_mobileIcon',
          },
          docImageId: 'mobileIconClickPayout',
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over component (Only fires if it contains a hoverElement)',
          config: {
            defaultPayout: true,
            trigger: 'content-productCardProduct',
            minHoverDuration: 2000,
          },
          payoutMetaValue: 'hoverPayout',
        },
      ],
    },
    expandableProductCard: {
      click: [
        {
          description: 'Click on component',
          config: {
            defaultPayout: true,
            trigger: 'content-expandableProductCard',
          },
          payoutMetaValue: 'clickPayout',
        },
        {
          description: 'Close component',
          config: {
            defaultPayout: true,
            trigger: 'content-expandableProductCard-close',
            external: false,
          },
          payoutMetaValue: 'closeButtonClickPayout',
        },
        {
          description: 'Navigate through slider',
          docImageId: 'sliderNavigationClickPayout',
          config: {
            defaultPayout: true,
            trigger: 'content-expandableProductCard-sliderNavigation',
            external: false,
          },
          payoutMetaValue: 'sliderClickPayout',
        },
        {
          description: 'Click on cta button',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-expandableProductCard-cta',
          },
          docImageId: 'hoverPayout',
          payoutMetaValue: 'stickyCTAClickPayout',
        },
        {
          description: 'Click on free space in slider',
          config: {
            defaultPayout: true,
            external: false,
            trigger: 'content-expandableProductCard-slider',
          },
          payoutMetaValue: 'clickPayout',
        },
      ],
      hover: [
        {
          description: 'Hover over CTA until expandable card appears',
          config: {
            defaultPayout: true,
            trigger: 'content-expandableProductCard-cta',
            minHoverDuration: 1250,
          },
          docImageId: 'hoverPayout',
          payoutMetaValue: 'stickyCTAHoverPayout',
        },
      ],
    },
  },
};
